/**
 * Reads the Rewardful referral ID for affiliate attribution.
 *
 * `rw.js` is loaded with `async`, so `window.Rewardful.referral` is not
 * populated on first paint. Reading it synchronously is a race: fast clickers
 * get `undefined` and the sale is never attributed. Rewardful's documented
 * contract is to wait for the `ready` event.
 *
 * The timeout matters. When rw.js is blocked (ad blockers routinely block it)
 * the `rewardful()` stub still exists and still queues callbacks, so `ready`
 * never fires. Checkout must not hang on an affiliate lookup, so a missing
 * referral resolves to `undefined` and the purchase proceeds unattributed.
 */
export function getRewardfulReferral(timeoutMs = 2000): Promise<string | undefined> {
  const w = window as unknown as {
    Rewardful?: { referral?: string };
    rewardful?: (event: string, cb: () => void) => void;
  };

  if (w.Rewardful?.referral) return Promise.resolve(w.Rewardful.referral);
  if (typeof w.rewardful !== 'function') return Promise.resolve(undefined);

  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(w.Rewardful?.referral || undefined);
    };

    const timer = setTimeout(finish, timeoutMs);
    w.rewardful!('ready', finish);
  });
}
